import React, { useEffect, useState } from 'react';
import { api } from './apiClient';
import { useStore } from './StoreContext';

export default function SyncBanner() {
    const { stores } = useStore();
    const [syncStatus, setSyncStatus] = useState({});
    const [repProgress, setRepProgress] = useState(null);

    // Self-scheduling poll. Delay is recomputed from the just-fetched payload
    // each tick — NOT from React state — so the effect can stay mounted for
    // the component's lifetime instead of tearing down on every state update.
    // A prior version listed syncStatus/repProgress in the deps array, which
    // turned every poll into an instant re-poll and saturated the backend.
    useEffect(() => {
        let cancelled = false;
        let timeoutId = null;

        const tick = async () => {
            if (cancelled) return;
            const [sJson, rJson] = await Promise.all([
                api.syncStatus().catch(() => null),
                api.repricerProgress().catch(() => null),
            ]);
            if (cancelled) return;

            if (sJson) setSyncStatus(sJson);
            if (rJson) setRepProgress(rJson);

            const anyRunning =
                (sJson && Object.values(sJson).some(
                    s => Object.values(s || {}).some(v => v?.status === 'running')
                )) || rJson?.running === true;
            const delay = anyRunning ? 3000 : 10000;
            timeoutId = setTimeout(tick, delay);
        };

        tick();
        return () => {
            cancelled = true;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, []);

    const storeName = (id) => stores.find(s => String(s.id) === String(id))?.display_name || `Store ${id}`;

    const chips = [];
    for (const sidStr of Object.keys(syncStatus || {})) {
        for (const t of ['amazon', 'competitors']) {
            const slot = syncStatus[sidStr]?.[t];
            if (!slot) continue;
            if (slot.status === 'running') {
                const detail = slot.phase ? `${slot.phase}${slot.detail ? ' · ' + slot.detail : ''}` : slot.detail;
                chips.push({
                    key: `${sidStr}-${t}`,
                    label: `Syncing ${t === 'amazon' ? 'Amazon' : 'Competitors'} — ${storeName(sidStr)}`,
                    detail, color: '#5c6ac4',
                });
            } else if (slot.status === 'error') {
                chips.push({
                    key: `${sidStr}-${t}-err`,
                    label: `${t === 'amazon' ? 'Amazon' : 'Competitors'} failed — ${storeName(sidStr)}`,
                    detail: slot.error, color: '#e04e39',
                });
            }
        }
    }
    if (repProgress?.running) {
        chips.push({
            key: 'repricer',
            label: 'Syncing Repricer',
            detail: repProgress.phase ? `${repProgress.phase}${repProgress.detail ? ' · ' + repProgress.detail : ''}` : null,
            color: '#5c6ac4',
        });
    }

    if (chips.length === 0) return null;

    return (
        <div style={{
            padding: '8px 16px', background: '#eef2ff', borderBottom: '1px solid #c7d2fe',
            display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 13, alignItems: 'center',
        }}>
            {chips.map(c => (
                <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                        display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                        background: c.color, animation: 'syncPulse 1.5s infinite',
                    }} />
                    <span style={{ color: '#333', fontWeight: 600 }}>{c.label}</span>
                    {c.detail && <span style={{ color: '#666' }}>· {c.detail}</span>}
                </div>
            ))}
            <style>{`@keyframes syncPulse { 0%,100% { opacity:1 } 50% { opacity:0.3 } }`}</style>
        </div>
    );
}
